<template>
  <BittsPopover
    overlay-class="c-list-user-permission-module__container"
    :options="listUsers"
    placement="bottomRight"
    :show-menu="isVisible"
  >
    <template #default>
      <BittsButton
        text="Share"
        size="small"
        type="neutral"
        :active="isVisible"
        @click="emit('invite-button-clicked')"
      />
    </template>
    <template #content>
      <div class="p-16 w-[480px] max-h-[600px]">
        <div class="flex pb-16 text-neutral-text-strong font-bold text-xl">
          <BittsButton
            v-if="!showUsers"
            data-testid="user-perm-back-button"
            :center-icon="['fak', 'arrow-left']"
            aria-label="Back"
            variant="ghost"
            class="mr-8"
            size="small"
            @click="onClearSelected"
          />
          Invite Team Members
        </div>
        <BittsMultiselect
          v-model="selectedTeamMemberOptions"
          form-label="Add Members"
          custom-filter="label"
          placeholder="Type names or emails"
          mode="multiple"
          :options="availableTeamMemberOptions"
          :show-checkboxes="false"
          :show-selected-options="false"
          :dropdown-class="
            !!selectedTeamMemberOptions.length
              ? 'c-list-user-permission-module__hide-dropdown'
              : ''
          "
        >
          <template #emptyStateContent>
            <p class="flex justify-center text-neutral-text-weak pb-12">
              {{ emptyStateMessage }}
            </p>
            <BittsButton
              text="Invite Team Members"
              size="x-small"
              class="w-full"
              type="neutral"
              :left-icon="['fak', 'add']"
              @click="onInviteTeamBtnClicked"
            />
          </template>
        </BittsMultiselect>
        <BittsDivider class="my-16" />
        <div v-if="showUsers" class="overflow-auto max-h-[375px]">
          <p class="text-neutral-text-strong pb-8 font-bold"> List Access </p>
          <ListUserPermissionsCard
            v-for="user in listUsers"
            :key="user.user_id"
            :user="user"
            :org="user.org"
            :loading="loadingState[user.user_id]"
            :editable="canAccessBeEdited(user)"
            @option-selected="onClickedRemoveUser"
          />
        </div>
        <div v-else class="flex justify-end">
          <BittsButton
            text="Cancel"
            class="mr-16"
            type="neutral"
            :loading="isLoading"
            @click="onClearSelected"
          />
          <BittsButton
            text="Send Invites"
            :loading="isLoading"
            @click="onClickedSendInvite"
          />
        </div>
      </div>
    </template>
  </BittsPopover>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsDivider,
  BittsMultiselect,
  BittsPopover,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';
import { AddSelectProperties } from '@crossbeam/types';

import { sortBy } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import ListUserPermissionsCard, {
  Option,
} from '@/components/shared-list/ListUserPermissionsCard.vue';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import useSharedList from '@/composables/useSharedList';
import { TEMP_PARTNER_VISIBILITY } from '@/constants/feature_flags';
import { EDITOR, OWNER } from '@/constants/shared_list';
import { captureException } from '@/errors';
import {
  useBillingStore,
  useCollaborateStore,
  useFeatureFlagStore,
  useFlashesStore,
  usePartnersStore,
  useRolesStore,
  useTeamStore,
} from '@/stores';
import {
  SharedListPermissionsPatchPayload,
  SharedListUserOrgAndLabel,
} from '@/types/shared_lists';
import { TeamAccessAuthorization, TeamAuthorizationUser } from '@/types/team';

const {
  isVisible = false,
  listId = '',
  listUsers = [],
} = defineProps<{
  isVisible: boolean;
  listId: string;
  listUsers: SharedListUserOrgAndLabel[];
}>();

const emit = defineEmits<(e: 'invite-button-clicked' | 'closed') => void>();
const teamStore = useTeamStore();
const selectedTeamMemberOptions = ref<string[]>([]);
const isLoading = ref(false);

type LoadingState = Record<string, boolean>;
const loadingState = ref<LoadingState>({});

const { currentOrg } = useAuth();
const collaborateStore = useCollaborateStore();
const router = useRouter();
const { iteratively } = useIteratively();
const { partnerOrg } = useSharedList();

const rolesStore = useRolesStore();
const { rolesById } = storeToRefs(rolesStore);

const flashesStore = useFlashesStore();

const roleIdsWithListAccess = computed(() => {
  const roles = [];
  for (const [roleId, role] of Object.entries(rolesById.value)) {
    const roleHasPermission = role.permissions.find(
      (permission) =>
        permission.resource === 'lists' &&
        permission.scopes.includes('write:lists'),
    );
    if (roleHasPermission) roles.push(roleId);
  }
  return roles;
});

const { isEnterpriseTier } = storeToRefs(useBillingStore());
const featureFlagStore = useFeatureFlagStore();
const partnerVisibilityEnabled = computed(
  () =>
    featureFlagStore.hasFeatureFlag(TEMP_PARTNER_VISIBILITY) ||
    isEnterpriseTier.value,
);

const teamMembers = ref<TeamAccessAuthorization[]>([]);
async function getTeamMembers() {
  if (partnerVisibilityEnabled.value) {
    const { getPartnerOrgById } = usePartnersStore();
    const organizationId: number | undefined =
      partnerOrg.value?.organization_id;
    if (!organizationId) return;
    const foundPartnerOrg = getPartnerOrgById(organizationId);
    const uuid = foundPartnerOrg?.uuid;
    if (!uuid) return;
    try {
      const { data } = await crossbeamApi.GET('/v0.1/team-access', {
        params: {
          query: { partner_org_uuid: uuid },
        },
      });
      teamMembers.value = data?.authorizations || [];
    } catch (e) {
      captureException(e);
    }
  } else {
    await teamStore.readySync;
    teamMembers.value = teamStore.coreUsers.map((user) => ({
      ...user,
      user: {
        ...user.user,
        first_name: user.user.first_name ?? null,
        last_name: user.user.last_name ?? null,
      },
    }));
  }
}
type UserForSelect = AddSelectProperties<TeamAuthorizationUser>;
const availableTeamMemberOptions = computed(() => {
  const unsortedUsers: UserForSelect[] = [];
  teamMembers.value.forEach((coreUser) => {
    const { user, role_id: roleId } = coreUser;
    if (!user) return;
    const userInList = listUsers.find(
      (listUser) =>
        listUser.user_id === user.id &&
        listUser.org?.organization_id === currentOrg.value.id,
    );
    if (!userInList && roleIdsWithListAccess.value.includes(roleId || '')) {
      unsortedUsers.push({
        ...user,
        value: `${user.id}__${user.first_name}__${user.last_name}__${user.email}`,
        label: `${user.first_name} ${user.last_name}`,
      });
    }
  });
  return sortBy(unsortedUsers, ['label']);
});

function canAccessBeEdited(user: SharedListUserOrgAndLabel) {
  return (
    user.org?.organization_id === currentOrg.value.id &&
    user.user_access !== OWNER
  );
}

const showUsers = computed(() => !selectedTeamMemberOptions.value.length);

function onClearSelected() {
  selectedTeamMemberOptions.value = [];
  loadingState.value = {};
}

function formattedIterativelyPayload(user: string) {
  const userId = user.split('__')[0];
  const userEmail = user.split('__')[3];
  return iteratively.userUpdatedSharedListAccess({
    event_site: EVENT_SITES.SHARED_LIST_SHARE_POPOVER,
    list_action: 'Added',
    list_id: listId,
    access_type: EDITOR,
    impacted_user_id: userId || '',
    impacted_user_email: userEmail || '',
  });
}

async function onClickedSendInvite() {
  isLoading.value = true;
  let firstName;
  let lastName;
  const body = selectedTeamMemberOptions.value.map((teamMember) => {
    const [userId, userFirstName, userLastName] = teamMember.split('__');
    firstName = userFirstName;
    lastName = userLastName;
    return { user_id: Number(userId), user_access: EDITOR };
  }) as SharedListPermissionsPatchPayload;
  try {
    await crossbeamApi.PATCH('/v0.1/lists/{list_id}/permissions', {
      body,
      params: {
        path: { list_id: listId },
      },
    });
    await collaborateStore.refreshCollaborateStore();
    selectedTeamMemberOptions.value.forEach((teamMember) =>
      formattedIterativelyPayload(teamMember),
    );
    flashesStore.addSuccessFlash({
      message: `${selectedTeamMemberOptions.value.length === 1 ? `${firstName} ${lastName}` : 'Users'} added to Static List`,
    });
  } catch (e) {
    captureException(e);
    flashesStore.addErrorFlash({
      message: 'Something went wrong, reach out to our support team for help',
    });
  } finally {
    onClearSelected();
    isLoading.value = false;
  }
}

async function onClickedRemoveUser(option: Option) {
  loadingState.value[option.user.user_id || ''] = true;
  try {
    const id = Number(option.user.user_id);
    await crossbeamApi.DELETE('/v0.1/lists/{list_id}/permissions', {
      params: {
        path: { list_id: listId },
        query: { user_id: [id] },
      },
    });
    await collaborateStore.refreshCollaborateStore();
    flashesStore.addSuccessFlash({
      message: `${option.user.first_name} ${option.user.last_name} removed from Static List`,
    });
    iteratively.userUpdatedSharedListAccess({
      event_site: EVENT_SITES.SHARED_LIST_SHARE_POPOVER,
      list_action: 'Removed',
      list_id: listId,
      impacted_user_id: option.user.user_id?.toString() || '',
      impacted_user_email: option.user.email,
    });
  } catch (e) {
    captureException(e);
    flashesStore.addErrorFlash({
      message: 'Something went wrong, reach out to our support team for help',
    });
  } finally {
    loadingState.value[option.user.user_id || ''] = false;
  }
}

const multipleTeamMembersInOrg = computed(
  () =>
    !availableTeamMemberOptions.value.length && teamStore.coreUsers.length > 1,
);

const emptyStateMessage = computed(() => {
  if (multipleTeamMembersInOrg.value) {
    return 'Looks like your whole team has been invited to collaborate';
  }
  return 'To collaborate with more teammates on this Static List, add more members to your organization';
});

async function onInviteTeamBtnClicked() {
  await router.push({ name: 'invite_user' });
}

onMounted(getTeamMembers);
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-list-user-permission-module__container {
  @apply pt-0 z-30;
}
</style>
<style lang="pcss">
@reference "../../css/theme.css";
.c-list-user-permission-module__hide-dropdown.ant-select-dropdown-empty {
  @apply hidden;
}
</style>
